import { useRef, useState, useEffect } from 'react'
import {
  getGaleria,
  createGaleriaItem,
  updateGaleriaItem,
  deleteGaleriaItem,
  uploadImage,
  type GaleriaItem,
} from '@/api/adminApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

function inferTipoFromUrl(url: string): 'imagem' | 'video' {
  const value = url.toLowerCase()
  return /\.(mp4|webm|ogg|mov|mkv|m4v)(\?|#|$)/i.test(value) ? 'video' : 'imagem'
}

export function AdminGaleriaPage() {
  const [list, setList] = useState<GaleriaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<GaleriaItem | null>(null)
  const [form, setForm] = useState({
    url: '',
    tipo: 'imagem' as 'imagem' | 'video',
    legenda: '',
    ordem: 0,
    ativo: true,
  })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { notifySaved, notifyDeleted, notifyError, notifyValidation } = useSaveFeedback()

  const load = () => {
    setError(null)
    getGaleria(true)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.url.trim()) {
      notifyValidation('Envie um ficheiro ou indique o URL do item.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        await updateGaleriaItem(editing.id, {
          url: form.url,
          tipo: form.tipo,
          legenda: form.legenda || undefined,
          ordem: form.ordem,
          ativo: form.ativo,
        })
        notifySaved('Item da galeria actualizado.')
      } else {
        await createGaleriaItem({
          url: form.url,
          tipo: form.tipo,
          legenda: form.legenda || undefined,
          ordem: form.ordem,
          ativo: form.ativo,
        })
        notifySaved('Item adicionado à galeria.')
      }
      setEditing(null)
      setForm({ url: '', tipo: 'imagem', legenda: '', ordem: 0, ativo: true })
      load()
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) return
    const fileList = Array.from(files)
    setUploadError(null)
    setUploading(true)
    setUploadProgress({ current: 0, total: fileList.length })
    try {
      if (fileList.length === 1) {
        const { url } = await uploadImage(fileList[0])
        setForm((f) => ({ ...f, url, tipo: inferTipoFromUrl(url) }))
        notifySaved('Ficheiro enviado.')
      } else {
        const baseOrdem = list.length
        for (let i = 0; i < fileList.length; i++) {
          setUploadProgress({ current: i + 1, total: fileList.length })
          const { url } = await uploadImage(fileList[i])
          const tipo = inferTipoFromUrl(url)
          await createGaleriaItem({
            url,
            tipo,
            legenda: undefined,
            ordem: baseOrdem + i,
            ativo: true,
          })
        }
        setForm({ url: '', tipo: 'imagem', legenda: '', ordem: list.length + fileList.length, ativo: true })
        load()
        notifySaved(
          fileList.length === 1 ? 'Ficheiro enviado e adicionado à galeria.' : `${fileList.length} itens adicionados à galeria.`
        )
      }
    } catch (err) {
      notifyError(err, 'Erro no upload')
      setUploadError(err instanceof Error ? err.message : 'Erro no upload.')
    } finally {
      setUploading(false)
      setUploadProgress(null)
      e.target.value = ''
    }
  }

  const handleEdit = (item: GaleriaItem) => {
    setEditing(item)
    setForm({
      url: item.url,
      tipo: item.tipo ?? inferTipoFromUrl(item.url),
      legenda: item.legenda ?? '',
      ordem: item.ordem ?? 0,
      ativo: !!item.ativo,
    })
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir este item da galeria?')) return
    setError(null)
    try {
      await deleteGaleriaItem(id)
      if (editing?.id === id) setEditing(null)
      load()
      notifyDeleted('Item removido da galeria.')
    } catch (e) {
      notifyError(e, 'Erro ao excluir')
      setError(e instanceof Error ? e.message : 'Erro ao excluir')
    }
  }

  if (loading) return <p className={styles.placeholderMsg}>A carregar…</p>

  return (
    <>
      <header className={styles.sectionHeader}>
        <h1 className={styles.sectionTitle}>Galeria</h1>
        <p className={styles.sectionSubtitle}>
          Faça upload de imagens ou vídeos (pode selecionar vários ao mesmo tempo).
          Os ficheiros são guardados no servidor e exibidos no app do cliente.
        </p>
      </header>
      {error && (
        <div className={styles.erroSection}>
          <p className={styles.erro}>{error}</p>
        </div>
      )}
      <div className={styles.sectionCard}>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label>Mídia (imagem ou vídeo) *</label>
            <div className={styles.uploadStack}>
              <div className={styles.uploadRow}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,.mov,.mkv"
                  multiple={!editing}
                  onChange={handleFileChange}
                  className={styles.uploadInputHidden}
                  aria-label="Fazer upload de mídia"
                />
                <button
                  type="button"
                  className={styles.btnUpload}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading && uploadProgress
                    ? `A enviar ${uploadProgress.current} de ${uploadProgress.total}…`
                    : uploading
                      ? 'A enviar…'
                      : form.url
                        ? 'Trocar mídia'
                        : editing
                          ? 'Carregar mídia'
                          : 'Carregar mídia (uma ou várias)'}
                </button>
                {form.url && (
                  <button
                    type="button"
                    className={styles.btnCancel}
                    onClick={() => setForm((f) => ({ ...f, url: '' }))}
                    disabled={uploading}
                  >
                    Remover
                  </button>
                )}
              </div>
              {uploadError && <p className={styles.uploadError}>{uploadError}</p>}
              {form.url ? (
                <div className={styles.bannerPreview}>
                  {form.tipo === 'video' ? (
                    <video src={resolveMediaUrl(form.url)} controls style={{ width: '100%', maxHeight: 260, display: 'block', background: 'var(--bg)' }} />
                  ) : (
                    <img src={resolveMediaUrl(form.url)} alt={form.legenda || 'Pré-visualização'} />
                  )}
                </div>
              ) : (
                <p className={styles.uploadEmptyHint}>Nenhuma mídia selecionada.</p>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Legenda</label>
            <input
              value={form.legenda}
              onChange={(e) => setForm((f) => ({ ...f, legenda: e.target.value }))}
              placeholder="Legenda (opcional)"
            />
          </div>
          <div className={styles.formGroup}>
            <label>Ordem</label>
            <input
              type="number"
              value={form.ordem}
              onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) || 0 }))}
            />
          </div>
          <div className={`${styles.formGroup} ${styles.formRow}`}>
            <input
              type="checkbox"
              id="galeria-ativo"
              checked={form.ativo}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
            />
            <label htmlFor="galeria-ativo">Ativo</label>
          </div>
          <div className={styles.formActions}>
            <button type="submit" className={styles.btnSalvar} disabled={saving}>
              {saving ? 'A guardar…' : editing ? 'Atualizar' : 'Adicionar'}
            </button>
            {editing && (
              <button
                type="button"
                className={styles.btnCancel}
                onClick={() => { setEditing(null); setForm({ url: '', tipo: 'imagem', legenda: '', ordem: 0, ativo: true }) }}
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>
      <div className={styles.sectionListCard}>
        <h2 className={styles.sectionListTitle}>Itens da galeria</h2>
        {list.length === 0 ? (
          <p className={styles.listEmpty}>Nenhum item. Adicione uma imagem ou vídeo acima.</p>
        ) : (
          <div className={styles.listScrollArea}>
            <ul className={styles.list}>
            {list.map((item) => (
              <li key={item.id} className={styles.listItem}>
                {item.tipo === 'video' ? (
                  <div style={{ width: 64, height: 64, borderRadius: 8, flexShrink: 0, background: 'var(--bg-elevated)', display: 'grid', placeItems: 'center', fontSize: 20 }}>
                    🎬
                  </div>
                ) : (
                  <img
                    src={resolveMediaUrl(item.url)}
                    alt={item.legenda ?? ''}
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.background = 'var(--bg-elevated)'
                      ;(e.target as HTMLImageElement).src = ''
                    }}
                  />
                )}
                <div className={styles.listItemContent}>
                  <div className={styles.listItemTitle}>
                    {item.legenda || '(sem legenda)'}{' '}
                    <span className={styles.listItemMeta}>[{item.tipo === 'video' ? 'vídeo' : 'imagem'}]</span>
                  </div>
                  <div className={styles.listItemMeta}>{item.url}</div>
                  {!item.ativo && <span className={styles.listItemMeta}>(inativo)</span>}
                </div>
                <div className={styles.listItemActions}>
                  <button type="button" onClick={() => handleEdit(item)} className={styles.btnSecondary}>
                    Editar
                  </button>
                  <button type="button" onClick={() => handleDelete(item.id)} className={styles.btnDanger}>
                    Excluir
                  </button>
                </div>
              </li>
            ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}
